import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowRight, Phone } from "lucide-react";
import { Link } from "react-router-dom";

const LINES = ["LOCAL WORK.", "FAIR PRICES.", "EVERY SEASON."];

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const chipY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-bone">
      <div
        className="pointer-events-none absolute -top-32 -right-32 w-[540px] h-[540px] rounded-full bg-limed-light blur-3xl opacity-60"
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
            className="flex flex-wrap items-center gap-2 mb-8"
          >
            <span className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold uppercase tracking-[0.2em] text-pine bg-sage px-3 py-1.5 rounded-full">
              <MapPin size={12} strokeWidth={2.5} />
              Oak Grove & Grain Valley, MO
            </span>
            <span className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-pine-dark bg-limed-light px-3 py-1.5 rounded-full">
              Teen-run local crew
            </span>
          </motion.div>

          <h1 data-testid="hero-headline" className="font-display font-extrabold tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl text-forest">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: EASE }}
                  className={`block ${i === 2 ? "text-limed" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
            className="mt-6 text-base sm:text-lg text-emerald-900/70 leading-relaxed max-w-xl"
          >
            Affordable seasonal services from a hard-working local crew. Pick a slot on our live
            calendar and we'll confirm your job — simple as that.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              data-testid="hero-book-cta"
              to="/schedule"
              className="group inline-flex items-center gap-2.5 bg-pine text-bone font-semibold px-7 py-3.5 rounded-full hover:bg-limed hover:text-pine-dark transition-colors duration-300 shadow-xl shadow-emerald-950/10"
            >
              Book on the Calendar
              <ArrowRight size={17} strokeWidth={2.5} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <a
              data-testid="hero-phone-link"
              href="tel:8164009576"
              className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-900/60 hover:text-pine transition-colors duration-200"
            >
              <Phone size={15} strokeWidth={2.5} />
              816-400-9576
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: EASE }}
            className="relative"
          >
            <div className="overflow-hidden rounded-[2.5rem] rounded-tr-[6rem] shadow-2xl shadow-emerald-950/20 border border-emerald-950/10">
              <motion.img
                style={{ y: imgY, scale: 1.15 }}
                src="https://images.unsplash.com/photo-1782594700873-bbeefef37e11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxyZWN5Y2xlJTIwYmluJTIwdHJhc2glMjBjYW4lMjBvdXRkb29yJTIwc3VidXJiYW4lMjBob3VzZSUyMGxhd258ZW58MHx8fHwxNzkwNjI4NzU4fDA&ixlib=rb-4.1.0&q=85"
                alt="All-Season Crew at work in the neighborhood"
                className="w-full h-[420px] lg:h-[520px] object-cover"
              />
            </div>
            <motion.div style={{ y: chipY }} className="absolute -bottom-6 -left-6 flex flex-col gap-3">
              <div className="animate-float bg-white rounded-2xl shadow-xl shadow-emerald-950/10 border border-emerald-950/10 px-5 py-3.5">
                <p className="font-display font-bold text-2xl text-pine leading-none">5</p>
                <p className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mt-1">Seasonal services</p>
              </div>
            </motion.div>
            <div className="absolute -top-5 -right-3 animate-float" style={{ animationDelay: "1.2s" }}>
              <div className="bg-pine text-bone rounded-2xl shadow-xl shadow-emerald-950/20 px-5 py-3.5">
                <p className="font-display font-bold text-2xl leading-none text-limed-bright">$15+</p>
                <p className="text-xs font-mono uppercase tracking-widest text-bone/60 mt-1">Starting prices</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
